import { addDays, dayOfWeek, diffDays, toUTC, todayISO, type ISODate } from '@keel/core';
import { useMemo } from 'react';
import { useFormatContext } from './money';

export type DateStyle = 'short' | 'medium' | 'long' | 'weekday' | 'dayMonth';

const STYLE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  weekday: { weekday: 'short', day: 'numeric', month: 'short' },
  dayMonth: { day: 'numeric', month: 'short' },
};

/** Format an ISODate calendar date (no timezone shift — formatted as UTC). */
export const formatDate = (d: ISODate, locale = 'en-AU', style: DateStyle = 'medium'): string =>
  new Intl.DateTimeFormat(locale, { ...STYLE_OPTIONS[style], timeZone: 'UTC' }).format(toUTC(d));

/** "today", "tomorrow", "in 3 days", "yesterday", "5 days ago". */
export const relativeDay = (d: ISODate, locale = 'en-AU', today: ISODate = todayISO()): string => {
  const n = diffDays(today, d);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(n, 'day');
};

/** The Sunday that ends the week containing `d` (Mon–Sun weeks). */
export const weekEnding = (d: ISODate = todayISO()): ISODate => {
  const dow = dayOfWeek(d);
  return dow === 0 ? d : addDays(d, 7 - dow);
};

/** Weeks run Monday–Sunday: the Sunday ending this week (so far) or the last full week. Home's card and the review share it. */
export type ReviewWeek = 'this' | 'last';
export const reviewWeekEnd = (today: ISODate, which: ReviewWeek): ISODate => {
  const thisEnd = weekEnding(today);
  return which === 'this' ? thisEnd : addDays(thisEnd, -7);
};
export const REVIEW_WEEK_LABEL: Record<ReviewWeek, string> = { this: 'This week so far', last: 'Last full week' };

export const isSunday = (d: ISODate = todayISO()): boolean => dayOfWeek(d) === 0;

export const timeOfDay = (now: Date = new Date()): 'morning' | 'afternoon' | 'evening' => {
  const h = now.getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
};

/** Date helpers bound to the current entity locale. */
export const useDates = () => {
  const { locale } = useFormatContext();
  return useMemo(
    () => ({
      locale,
      format: (d: ISODate, style: DateStyle = 'medium') => formatDate(d, locale, style),
      relative: (d: ISODate) => relativeDay(d, locale),
      today: todayISO(),
    }),
    [locale],
  );
};
